import { AppliesToType, calculateOption, FormFinancialLineItem, PlanOptionType, PlansQuery } from '@fox/finance';
import { QuestionStyle } from '../../form-runtime-context';
import { ConditionalDefinitions } from '../form-block/conditionals/contracts';
import { ProvidesSelect } from '../question-block/contracts';
import { findAllPlanOptions } from './helpers';

export type Plan = Omit<PlansQuery['all_plans'][0], 'id'> & { id: string | number };
export type Fee = Plan['fees'][0];
export type SubscriptionBlockProps = {
	columnIndex?: number;
	dragId?: string;
	id: string;
	isDragging?: boolean;
	type: string;
	props: { name: string; title: string; type: string };
	token?: string;
};

export type SubscriptionFieldMeta = {
	allowedPlans: string | null;
	required: boolean;
	showAutoRenew: boolean;
	labelOverrides: Record<string, string | null>;
	/** @deprecated No longer used */
	numberOfQuestions?: number;
	provides?: Record<string, ProvidesSelect>;
	requires?: string[];
	immediatePayment: boolean;
	conditionalDefinitions?: ConditionalDefinitions;
	defaultToAutoRenew?: boolean;
	defaultPlanId?: Plan['id'];
	allowedOptions?: Record<Plan['planKey'], number[]>;
	defaultOptions?: Record<Plan['planKey'], number[]>;
	optionDisplayType?: 'Checkbox' | 'Select';
	/**
	 * The default status of the subscription to be created. Default is active if not specified or null
	 */
	defaultSubCreateStatus?: string | null;
};

export type SubscriptionAnswer = {
	feeId?: number;
	planKey?: string;
	autoRenew?: boolean;
	token?: string;
	optionIds?: number[];
	/**
	 * The default status of the subscription to be created. Default is active if not specified or null
	 */
	defaultSubCreateStatus?: string | null;
};
export type FieldPropsType = {
	metadataJson: string;
	name: string;
	order: number;
	title: string;
	type: string;
};
export type BuildStylesType = {
	labelStyles: {
		'&.MuiTypography-root.MuiTypography-body1': QuestionStyle;
	};
	answerStyles: {
		'& .MuiTypography-root.MuiTypography-body1': QuestionStyle;
	};
};
/**
 * Builds the subscription line items
 * @param answer - Form answer
 * @param plans - Available plans
 * @param immediatePayment - If payment is to be taken immediately or not
 * @returns line item object array
 */
const buildLineItems = (
	answer: SubscriptionAnswer,
	plans: Plan[],
	immediatePayment: boolean,
	prorate?: number
): FormFinancialLineItem[] => {
	const chosenPlan = plans?.find(p => p.planKey === answer.planKey);
	const availableIntervals = chosenPlan?.fees ?? [];
	const currentInterval = availableIntervals?.find(interval => interval.id === answer.feeId);
	const allPlanOptions = findAllPlanOptions(plans);

	const toRet: FormFinancialLineItem[] = [];

	if (chosenPlan && currentInterval) {
		const currentFee = prorate ?? currentInterval.fee;
		const renewalFee = currentInterval.renewalFee ?? 0;

		const baseAmount = currentFee === 0 && renewalFee > 0 ? renewalFee : currentFee;
		// The base fee will not include the renewal fee - we are treating it as the fee if they
		const extraRenewalFee = currentFee > 0 && renewalFee > 0;

		toRet.push({
			amount: baseAmount,
			comment: `${chosenPlan.name} - ${currentInterval.interval}`,
			lineTypeId: renewalFee > 0 && !extraRenewalFee ? 5 : 2,
			immediatePayment,
			autoRenew: answer.autoRenew,
			targetType: AppliesToType.Subscription,
			targetId: `${answer.planKey}:${answer.feeId}`
		});
		if (extraRenewalFee) {
			toRet.push({
				amount: renewalFee,
				comment: `Annual Renewal Fee`,
				lineTypeId: 5,
				immediatePayment,
				autoRenew: answer.autoRenew,
				targetType: AppliesToType.Subscription,
				targetId: `${answer.planKey}:${answer.feeId}`
			});
		}

		for (const option of answer.optionIds ?? []) {
			const matchingOption = allPlanOptions.find(p => p.id === option);

			if (!matchingOption) continue;

			toRet.push({
				amount: calculateOption(matchingOption.amount, matchingOption.type, baseAmount),
				comment: `${matchingOption?.name}`,
				lineTypeId: PlanOptionType.Discount === matchingOption.type ? 4 : 1,
				immediatePayment,
				autoRenew: answer.autoRenew
			});
		}
	}

	return toRet;
};
export const buildProvidedData = (answer: SubscriptionAnswer, plans: Plan[], immediatePayment: boolean, prorate?: number) => {
	// Have we selected a fee
	if (answer.feeId) {
		return {
			lineItems: [...buildLineItems(answer, plans, immediatePayment, prorate)]
		};
	} else return null;
};
